import React, { useEffect, useState } from "react";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import IndeterminateCheckBoxOutlinedIcon from "@mui/icons-material/IndeterminateCheckBoxOutlined";
import {
  checkMoneyDataForSummaryTable,
  findObjInArray,
  getCardTypeIcon,
} from "utils/utils";
import s from "./Datatable.module.scss";
import ButtonMAS from "ButtonMAS/ButtonMAS";
import Pagination from "./Pagination/Pagination";
import NoResult from "./NoResult/NoResult";
import Checkbox from "components/Input/Checkbox/Checkbox";
import { t } from "i18next";
import SubTable from "./SubTable/SubTable";

const getParsedColumns = (columns, columnsInfo) => {
  if (columnsInfo && columnsInfo?.length === columns?.length) {
    return [...columns]
      .sort(
        (a, b) =>
          columnsInfo.map((e) => e.key).indexOf(a.key) -
          columnsInfo.map((e) => e.key).indexOf(b.key),
      )
      .filter(
        (column) =>
          !columnsInfo.some((info) => info.key === column.key && info.hidden),
      );
  } else {
    return columns;
  }
};

const Datatable = ({
  columns,
  columnsPreference,
  datasource,
  total,
  sorting,
  onsortFn,
  defaultSortingOrder,
  pagination,
  setCurrentPage,
  setPageSize,
  pageSizeInfoRef,
  handleSearch,
  tableOnChange,
  search,
  checkbox, // checkbox will be shown if this flag is true
  selectedArray, // contains all the selected row
  updateSelectedArrayFn,
  summaryTable, // summary table will be shown if this flag is true
  isMoreThanOneCurrency,
  restoreNumberOfSelectedCurrencyFn,
  subTable,
  subTableDisplayRow,
  handleOpenSubTableRow,
  subTableColumn,
  subTableContent,
  subTableSort,
  subTableSortCustomOrder,
  isEditing,
  subTableAddBtn,
  handleSubtableAddFn,
  isAdding,
  getCardTypeOptions,
  addMdrValue,
  handleAddMdrOnchange,
  handleAddMdrOnBlur,
  navigateToDetail,
}) => {
  const parsedColumns = getParsedColumns(columns, columnsPreference);
  const [clickTimeout, setClickTimeout] = useState(null);
  let sortingKey;
  let sortingOrder;
  if (sorting) {
    sortingKey = sorting.split(",")[0];
    sortingOrder = sorting.split(",")[1];
  }
  const noOfSummaryColumn = parsedColumns.filter(
    (column) => column.summary,
  ).length;

  const handleCheckboxOncheck = (e, data) => {
    if (e.target.checked) {
      updateSelectedArrayFn([...selectedArray, data]);
    } else {
      const { index } = findObjInArray(selectedArray, data);
      const tempArray = selectedArray.filter((_, i) => i !== index);
      updateSelectedArrayFn(tempArray);
    }
  };

  const checkAll = () => {
    if (selectedArray.length !== datasource?.length) {
      updateSelectedArrayFn(datasource);
    } else {
      updateSelectedArrayFn([]);
    }
  };

  useEffect(() => {
    if (updateSelectedArrayFn) {
      updateSelectedArrayFn([]);
    }
    if (restoreNumberOfSelectedCurrencyFn) {
      restoreNumberOfSelectedCurrencyFn();
    }
  }, [pagination]);

  const handleOnSingleClick = (data) => {
    const highlightedString = window.getSelection().toString();
    const isHighlighted = highlightedString.trim() !== "";
    if (isHighlighted) {
      return;
    }

    // Clear any existing single click timeout
    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
    }

    // Set a new timeout for single click
    const timeoutId = setTimeout(() => {
      if (typeof navigateToDetail === "function") {
        navigateToDetail(data);
      } else {
        console.warn("navigateToDetail is not defined");
      }
      setClickTimeout(null); // Reset timeout state after processing
    }, 300);
    setClickTimeout(timeoutId);
  };

  const handleOnDoubleClick = () => {
    // Clear single click timeout to prevent single click logic from executing
    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
    }
  };

  return (
    <div>
      <div className={s.datatableContainer}>
        <table className={s.datatable}>
          <thead>
            <tr className={s.tr}>
              {checkbox && (
                <th className={s.theadCheckbox}>
                  <Checkbox
                    oncheckFn={checkAll}
                    checked={selectedArray?.length === datasource?.length}
                  />
                </th>
              )}
              {subTable && <th className={s.subTableChecker}></th>}
              {parsedColumns.map((column) => {
                return (
                  <th key={column.key}>
                    <div
                      className={[
                        s.header,
                        column.type && column.type === "amount"
                          ? s.alignRight
                          : "",
                        column.key === "action" ? s.actionHeader : "",
                      ].join(" ")}
                    >
                      <div className={s.title}>
                        {column.title}
                        {column.indicator && <sup>{column.indicator}</sup>}
                      </div>
                      {column.sorter && sortingKey !== column.key && (
                        <ButtonMAS
                          variant="text-only"
                          className={s.sortIcon}
                          onClick={() => onsortFn(column.key, "asc")}
                        >
                          <UnfoldMoreIcon sx={{ fontSize: 18 }} />
                        </ButtonMAS>
                      )}
                      {column.sorter &&
                        sortingKey === column.key &&
                        sortingOrder === "asc" && (
                          <ButtonMAS
                            variant="text-only"
                            className={s.sortIcon}
                            onClick={() => onsortFn(column.key, "desc")}
                          >
                            <ArrowDropUpIcon
                              sx={{ fontSize: 24, color: "#36b3be" }}
                            />
                          </ButtonMAS>
                        )}
                      {column.sorter &&
                        sortingKey === column.key &&
                        sortingOrder === "desc" && (
                          <ButtonMAS
                            variant="text-only"
                            className={s.sortIcon}
                            onClick={() =>
                              onsortFn(
                                defaultSortingOrder[0],
                                defaultSortingOrder[1],
                              )
                            }
                          >
                            <ArrowDropDownIcon
                              sx={{ fontSize: 24, color: "#36b3be" }}
                            />
                          </ButtonMAS>
                        )}
                    </div>
                  </th>
                );
              })}
              <th style={{ width: "100%" }}></th>
            </tr>
          </thead>
          {datasource.length !== 0 && (
            <tbody>
              {datasource.map((data, index) => {
                //content is for subtable
                const content = Array.isArray(subTableContent)
                  ? subTableContent
                  : data[subTableContent];

                const { merchantId } = data;
                return (
                  <React.Fragment key={index}>
                    <tr
                      className={[
                        // index % 2 === 0 ? s.even : s.odd,
                        s.tr,
                        selectedArray?.some((item) => item.id === data.id)
                          ? s.selected
                          : "",
                      ].join(" ")}
                    >
                      {checkbox && (
                        <td className={s.tbodyCheckbox}>
                          <Checkbox
                            oncheckFn={(e) => handleCheckboxOncheck(e, data)}
                            checked={findObjInArray(selectedArray, data).result}
                          />
                        </td>
                      )}
                      {subTable && (
                        <td className={s.subTableChecker}>
                          {subTableDisplayRow === data.id ? (
                            <ButtonMAS
                              variant="text-only"
                              onClick={() => handleOpenSubTableRow(data.id)}
                            >
                              <IndeterminateCheckBoxOutlinedIcon
                                style={{ fontSize: 18, color: "#36b3be" }}
                              />
                            </ButtonMAS>
                          ) : (
                            <ButtonMAS
                              variant="text-only"
                              onClick={() => handleOpenSubTableRow(data.id)}
                            >
                              <AddBoxOutlinedIcon
                                style={{ fontSize: 18, color: "#666" }}
                              />
                            </ButtonMAS>
                          )}
                        </td>
                      )}
                      {parsedColumns.map((column) => {
                        const k = column.key;
                        if (k === "cardType" || k === "card_type") {
                          const paymentLogo = getCardTypeIcon(data[k]);
                          const paymentType = data[k]?.toLowerCase?.() ?? "";
                          return (
                            <td key={k}>
                              <div className={s.paymentLogo}>
                                <img
                                  src={paymentLogo}
                                  height={24}
                                  alt={paymentType.toUpperCase()}
                                />
                              </div>
                            </td>
                          );
                        }
                        // else if (k === "status") {
                        //   const status = data[k].toLowerCase();
                        //   return (
                        //     <td key={k} className={s.td}>
                        //       <div className={s.statusIconContainer}>
                        //         <div
                        //           className={[
                        //             s.statusIcon,
                        //             status === "success"
                        //               ? s.success
                        //               : status === "none"
                        //               ? s.none
                        //               : status === "refunded"
                        //               ? s.refunded
                        //               : status === "declined"
                        //               ? s.declined
                        //               : "",
                        //           ].join(" ")}
                        //         >
                        //           {t(`component.datatable.status.${data[k]}`)}
                        //         </div>
                        //       </div>
                        //     </td>
                        //   );
                        // }
                        else {
                          return (
                            <td
                              className={navigateToDetail ? s.clickable : ""}
                              key={k}
                              onClick={() => {
                                if (k !== "action") handleOnSingleClick(data);
                              }}
                              onDoubleClick={() => {
                                handleOnDoubleClick();
                              }}
                            >
                              <div
                                className={[
                                  s.td,
                                  column.type && column.type === "amount"
                                    ? s.alignRight
                                    : "",
                                ].join(" ")}
                              >
                                <div className={s.content}>
                                  {column.render(data, k, column)}
                                </div>
                              </div>
                            </td>
                          );
                        }
                      })}
                      <td style={{ width: "100%" }}></td>
                    </tr>
                    {subTable && (
                      <tr
                        className={
                          subTableDisplayRow !== data.id ? s.noshowRow : ""
                        }
                      >
                        <td colSpan={parsedColumns.length + 2}>
                          {
                            <SubTable
                              columns={subTableColumn}
                              datasource={content}
                              sortFn={subTableSort}
                              customSortOrder={subTableSortCustomOrder}
                              displayRowMerchantId={merchantId}
                              isEditing={isEditing}
                              subTableAddBtn={subTableAddBtn}
                              handleSubtableAddFn={handleSubtableAddFn}
                              isAdding={isAdding}
                              getCardTypeOptions={getCardTypeOptions}
                              addMdrValue={addMdrValue}
                              handleAddMdrOnchange={handleAddMdrOnchange}
                              handleAddMdrOnBlur={handleAddMdrOnBlur}
                            />
                          }
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          )}
        </table>
      </div>
      {summaryTable && selectedArray?.length !== 0 && (
        <div>
          <table className={s.footerTable}>
            <thead>
              <tr className={s.footerTableHeaderTr}>
                {parsedColumns.map((column) => {
                  if (column.summary) {
                    return (
                      <td
                        key={column.key}
                        className={s.footerTableHeader}
                        style={{ width: `calc(100% / ${noOfSummaryColumn})` }}
                      >
                        {column.title}
                        {column.indicator && <sup>{column.indicator}</sup>}
                      </td>
                    );
                  }
                })}
              </tr>
            </thead>
            <tbody>
              <tr className={s.footerTableContentTr}>
                {!isMoreThanOneCurrency ? (
                  <>
                    {parsedColumns.map((column) => {
                      if (column.summary) {
                        const { key } = column;
                        let result = 0;
                        selectedArray?.forEach((item) => {
                          result += item[key];
                        });
                        return (
                          <td
                            key={key}
                            className={s.footerTableContent}
                            style={{
                              width: `calc(100% / ${noOfSummaryColumn})`,
                            }}
                          >
                            {checkMoneyDataForSummaryTable(
                              result,
                              selectedArray[0][column.currencyKey],
                            )}
                            {/* {Number(result)
                              .toFixed(2)
                              .replace(/\B(?=(\d{3})+(?!\d))/g, ",")} */}
                          </td>
                        );
                      }
                    })}
                  </>
                ) : (
                  <>
                    {parsedColumns.map((column) => {
                      if (column.summary) {
                        return (
                          <td key={column.key} className={s.footerTableContent}>
                            {t("component.datatable.N.A")}
                          </td>
                        );
                      }
                    })}
                  </>
                )}
              </tr>
            </tbody>
          </table>
          {isMoreThanOneCurrency && (
            <div className={s.footerTableErrorMessage}>
              <span>
                {t("component.datatable.footer.table.error.message.1")}
              </span>
            </div>
          )}
        </div>
      )}
      {datasource.length === 0 && <NoResult search={search} />}
      {pagination && (
        <Pagination
          pagination={pagination}
          setCurrentPage={setCurrentPage}
          setPageSize={setPageSize}
          pageSizeInfoRef={pageSizeInfoRef}
          handleSearch={handleSearch}
          tableOnChange={tableOnChange}
          total={total}
        />
      )}
    </div>
  );
};

export default React.memo(Datatable);
